import { useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d6u3kfv5ehaffuo435ag';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function LeadForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [courtValue, setCourtValue] = useState<string>('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');

    const form = e.currentTarget;
    const data = new URLSearchParams();
    const elements = form.elements as HTMLFormControlsCollection;

    Array.from(elements).forEach((el) => {
      const input = el as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      if (input.name) {
        data.append(input.name, input.value);
      }
    });
    // Add the court value explicitly since it's managed in state
    data.set('private_court', courtValue);

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCourtValue('');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const inputClass =
    'w-full font-inter text-sm text-[#2C2C2C] placeholder-[#9B9B9B] border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:border-[#D4845C] transition-colors duration-200 bg-white';

  return (
    <section id="lead-form" className="py-28 bg-[#FAF8F5]">
      <div className="max-w-3xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="font-inter text-xs text-[#D4845C] uppercase tracking-[0.2em] font-medium mb-5">
            Register Your Interest
          </p>
          <h2 className="font-cormorant text-5xl font-light text-[#2C2C2C] leading-[1.1] mb-5">
            Join the PadelVillas<br />
            <em className="italic">Founding Partner Programme.</em>
          </h2>
          <p className="font-inter font-light text-[#6B6B6B] text-base max-w-lg mx-auto leading-relaxed">
            Complete the form below and our team will review your property and be in touch within 48 hours.
          </p>
        </div>

        {/* Success message */}
        {status === 'success' && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-8 text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-3">
              <i className="ri-check-line text-green-600 text-xl" />
            </div>
            <h3 className="font-cormorant text-2xl text-[#2C2C2C] font-semibold mb-2">Registration Received</h3>
            <p className="font-inter text-sm text-[#6B6B6B]">
              Thank you for your interest. Our team will review your villa and contact you within 48 hours.
            </p>
          </div>
        )}

        {/* Form */}
        {status !== 'success' && (
          <form
            onSubmit={handleSubmit}
            method="POST"
            data-readdy-form
            className="bg-white rounded-2xl p-8 lg:p-10"
          >
            <div className="grid md:grid-cols-2 gap-5">
              {/* Owner Name */}
              <div className="flex flex-col gap-1.5">
                <label className="font-inter text-xs font-medium text-[#2C2C2C] uppercase tracking-wider">
                  Owner Name <span className="text-[#D4845C]">*</span>
                </label>
                <input
                  type="text"
                  name="owner_name"
                  required
                  placeholder="Your full name"
                  className={inputClass}
                />
              </div>

              {/* Email */}
              <div className="flex flex-col gap-1.5">
                <label className="font-inter text-xs font-medium text-[#2C2C2C] uppercase tracking-wider">
                  Email Address <span className="text-[#D4845C]">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="your@email.com"
                  className={inputClass}
                />
              </div>

              {/* Phone */}
              <div className="flex flex-col gap-1.5">
                <label className="font-inter text-xs font-medium text-[#2C2C2C] uppercase tracking-wider">
                  Phone / WhatsApp <span className="text-[#D4845C]">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  placeholder="+34 600 000 000"
                  className={inputClass}
                />
              </div>

              {/* Villa Name */}
              <div className="flex flex-col gap-1.5">
                <label className="font-inter text-xs font-medium text-[#2C2C2C] uppercase tracking-wider">
                  Villa Name <span className="text-[#D4845C]">*</span>
                </label>
                <input
                  type="text"
                  name="villa_name"
                  required
                  placeholder="e.g. Villa La Serena"
                  className={inputClass}
                />
              </div>

              {/* Location */}
              <div className="flex flex-col gap-1.5">
                <label className="font-inter text-xs font-medium text-[#2C2C2C] uppercase tracking-wider">
                  Location <span className="text-[#D4845C]">*</span>
                </label>
                <input
                  type="text"
                  name="location"
                  required
                  placeholder="e.g. Marbella, Spain"
                  className={inputClass}
                />
              </div>

              {/* Listing Link */}
              <div className="flex flex-col gap-1.5">
                <label className="font-inter text-xs font-medium text-[#2C2C2C] uppercase tracking-wider">
                  Listing Link
                </label>
                <input
                  type="url"
                  name="listing_link"
                  placeholder="Airbnb, website, etc."
                  className={inputClass}
                />
              </div>

              {/* Guest Capacity */}
              <div className="flex flex-col gap-1.5">
                <label className="font-inter text-xs font-medium text-[#2C2C2C] uppercase tracking-wider">
                  Guest Capacity <span className="text-[#D4845C]">*</span>
                </label>
                <select name="guest_capacity" required className={inputClass}>
                  <option value="">Select capacity</option>
                  <option value="under-8">Under 8 guests</option>
                  <option value="8-10">8 to 10 guests</option>
                  <option value="10-14">10 to 14 guests</option>
                  <option value="14-20">14 to 20 guests</option>
                  <option value="20+">20+ guests</option>
                </select>
              </div>

              {/* Private Court */}
              <div className="flex flex-col gap-1.5">
                <label className="font-inter text-xs font-medium text-[#2C2C2C] uppercase tracking-wider">
                  Private Padel Court on Property <span className="text-[#D4845C]">*</span>
                </label>
                <input type="hidden" name="private_court" value={courtValue} />
                <div className="flex items-center gap-3">
                  {['Yes', 'No'].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setCourtValue(opt)}
                      className={`whitespace-nowrap flex-1 py-3 rounded-lg border text-sm font-inter font-medium transition-all duration-200 cursor-pointer ${
                        courtValue === opt
                          ? 'bg-[#D4845C] border-[#D4845C] text-white'
                          : 'border-gray-200 text-[#6B6B6B] hover:border-[#D4845C] hover:text-[#D4845C]'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Message */}
            <div className="flex flex-col gap-1.5 mt-5">
              <label className="font-inter text-xs font-medium text-[#2C2C2C] uppercase tracking-wider">
                Tell Us About Your Villa
              </label>
              <textarea
                name="message"
                rows={4}
                maxLength={500}
                placeholder="Share anything that makes your property exceptional: the court, views, amenities, unique features..."
                className={`${inputClass} resize-none`}
              />
              <p className="font-inter text-xs text-[#9B9B9B] text-right">Max 500 characters</p>
            </div>

            {/* Error */}
            {status === 'error' && (
              <p className="mt-4 font-inter text-sm text-red-500 text-center">
                Something went wrong. Please try again or contact us directly.
              </p>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={status === 'submitting'}
              className="whitespace-nowrap w-full mt-8 bg-[#D4845C] text-white font-inter font-medium text-sm py-4 rounded-full hover:bg-[#c4734b] transition-colors duration-200 cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {status === 'submitting' ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Register Your Villa
                  <i className="ri-arrow-right-line" />
                </>
              )}
            </button>
            <p className="mt-4 font-inter text-xs text-[#9B9B9B] text-center">
              Your information is kept confidential and will only be used to assess your villa&apos;s suitability.
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
